import { Icon, Form, FormApi, If, onMount, watch } from 'cdui-js';
import { showActionSheet } from '../../../../components/ActionSheet';
import { Countries } from '../../../../components/SelectCountry/Component';
import {
  addAttachmentFile,
  fillAddressEmailIfEmpty,
  fillAddressPhoneIfEmpty,
  isAddressEmailLocked,
  normalizeAddressType,
  lastProcess,
  nextProcess,
  pState,
  removeAttachmentFile,
  setFormApi,
} from '../../store';
import { UploadProof } from '../../UploadProof';
import { SelectCountry } from '../../../../components/SelectCountry';
import { LastNextBtns } from '../../components/LastNextBtns';
import { saveUserInfo } from '../../../../../../api/open-account';
import { orignCountryList } from '../../../../../../stores/bus-config';
import { OpenFormItem } from '../../components/OpenFormItem';
import { FieldReEditHint } from '../../components/FieldReEditHint';
import { DiFiInput } from '../../../../../../components/DiFi/DiFiInput';
import { DiFiCheckBox } from '../../../../../../components/DiFi/DiFiCheckBox';
import { DiFiTooltip } from '../../../../../../components/DiFi/DiFiTooltip';
import { showToast } from '../../../../components/Toast';
import i18n from '../../../../../../i18n';
import { isOpenProgressReEdit } from '../../reEditMode';

const flatCountries = () =>
  Object.values(orignCountryList).flatMap((group: any) => (Array.isArray(group) ? group : []));

/** 国家字典 extra 是不带 + 的区号，如 86；表单与注册 countryCode 统一存 +86 */
const toDialCode = (extra: unknown) => {
  const digits = String(extra ?? '').replace(/^\+/, '').trim();
  return digits ? `+${digits}` : '';
};

const dialFromCountryValue = (code: string) => {
  const hit = flatCountries().find((c) => String(c.code ?? c.value ?? '') === String(code));
  return toDialCode(hit?.extra);
};

/** 通讯地址类型：单选，方框样式，同一时间只保留一项 */
const ADDRESS_TYPE_OPTIONS = ['RESIDENTIAL', 'BUSINESS', 'OTHER'] as const;
const ADDRESS_TYPE_OTHER = 'OTHER';

const addressTypeLabel = (code: string) => {
  const A = i18n.OpenProgress.Info.Address;
  if (code === 'RESIDENTIAL') return A.OptResidential;
  if (code === 'BUSINESS') return A.OptBusiness;
  if (code === ADDRESS_TYPE_OTHER) return A.OptOther;
  return code;
};

const isAddressTypeOther = () => pState.addressForm.addressType === ADDRESS_TYPE_OTHER;

/** 仅「其他」需要填写详细通讯地址；住宅 / 商业地址不展示输入框 */
const setMailingDetail = (value: string) => {
  pState.addressForm.correspondenceAddress = value;
  pState.addressForm.addressTypeOther = value;
};

const clearMailingDetail = () => setMailingDetail('');

const selectAddressType = (code: string) => {
  if (pState.addressForm.addressType === code) return;
  pState.addressForm.addressType = code;
  if (code !== ADDRESS_TYPE_OTHER) clearMailingDetail();
};

const countryValueFromDial = (dial: string) => {
  const digits = String(dial || '').replace(/^\+/, '').trim();
  if (!digits) return '';
  const hit = flatCountries().find((c) => String(c.extra ?? '').replace(/^\+/, '') === digits);
  return String(hit?.code ?? hit?.value ?? '');
};

/** 有中文地址回填时勾选（取消勾选不清空 input，仅保存时按勾选决定是否传空） */
const syncAddressCnCheckedFromValue = () => {
  if (String(pState.addressForm.addressCn || '').trim()) {
    pState.addressCnChecked = true;
  }
};

/** 组装地址保存 payload；未勾选「住址证明是中文」时 addressCn 传空 */
export const buildAddressSavePayload = () => {
  const data = JSON.parse(JSON.stringify(pState.addressForm));
  const detail =
    data.addressType === ADDRESS_TYPE_OTHER
      ? String(data.correspondenceAddress || data.addressTypeOther || '').trim()
      : '';
  data.correspondenceAddress = detail;
  data.addressTypeOther = detail;
  pState.addressForm.correspondenceAddress = detail;
  pState.addressForm.addressTypeOther = detail;
  if (!pState.addressCnChecked) {
    data.addressCn = '';
    pState.addressForm.addressCn = '';
  }
  return data;
};

export const Address = (props) => {
  let formApi: FormApi = null;
  let actionSheetIns: { close: () => void } | null = null;

  const toggleAddressCn = () => {
    pState.addressCnChecked = !pState.addressCnChecked;
  };

  watch(
    () => pState.addressForm.addressCn,
    () => syncAddressCnCheckedFromValue(),
  );

  onMount(() => {
    fillAddressPhoneIfEmpty();
    fillAddressEmailIfEmpty();
    syncAddressCnCheckedFromValue();
    const normalized = normalizeAddressType(pState.addressForm.addressType);
    if (normalized !== pState.addressForm.addressType) {
      pState.addressForm.addressType = normalized;
    }
    if (!isAddressTypeOther()) {
      clearMailingDetail();
      return;
    }
    const detail = String(
      pState.addressForm.correspondenceAddress || pState.addressForm.addressTypeOther || '',
    );
    if (detail && detail !== pState.addressForm.correspondenceAddress) {
      setMailingDetail(detail);
    }
  });

  const openAreaCode = () => {
    const saved = pState.addressForm.phoneCountry;
    const current = countryValueFromDial(saved) || saved;
    actionSheetIns = showActionSheet({
      title: () => <div>{i18n.Login.SelectCountryRegion}</div>,
      contentClass: 'action-sheet--select-country',
      content: () => (
        <Countries
          value={current}
          showDialCode
          change={(e) => {
            const dial = dialFromCountryValue(e);
            pState.addressForm.phoneCountry = dial || (/^\d+$/.test(String(e)) ? toDialCode(e) : e);
            actionSheetIns?.close();
          }}
        />
      ),
    });
  };

  const getAddressImages = () =>
    (pState.attachmentList || [])
      .filter((i) => i.attachType === 'address_prove' && i.fileUrl)
      .map((i) => i.fileUrl as string);

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="font-s-L font-bold color-D margin-b-L">
        {i18n.OpenProgress.Info.Address.Title}
        {props.stepText}
      </div>

      <Form data={pState.addressForm} align="top" rules={{}} api={(api) => { formApi = api; setFormApi(api); }}>
        <OpenFormItem label={i18n.OpenProgress.Info.Address.LabelCountry} field="country" required>
          <SelectCountry value={pState.addressForm.country} change={(e) => (pState.addressForm.country = e)} />
        </OpenFormItem>

        <OpenFormItem label={i18n.OpenProgress.Info.Address.LabelAddress} field="address" required>
          <div>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={80}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.addressForm.address}
              onUpdateValue={(v) => (pState.addressForm.address = v)}
            />
          </div>
        </OpenFormItem>

        <DiFiCheckBox
          type="checkbox"
          label={i18n.OpenProgress.Info.Address.LabelAddressCnProof}
          checked={pState.addressCnChecked}
          class="font-s color-D padding-y-XS"
          style={{ width: '100%' }}
          onChange={toggleAddressCn}
        />

        <If when={pState.addressCnChecked}>
          <OpenFormItem
            label={i18n.OpenProgress.Info.Address.LabelAddressCn}
            field="addressCn"
            required
          >
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={80}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.addressForm.addressCn}
              onUpdateValue={(v) => (pState.addressForm.addressCn = v)}
            />
          </OpenFormItem>
        </If>

        <OpenFormItem
          label={i18n.OpenProgress.Info.Address.LabelMailingAddress}
          field="addressType"
          required
          class={isAddressTypeOther() ? 'open-form-item-compact' : ''}
        >
          <div class="flex-column">
            {ADDRESS_TYPE_OPTIONS.map((code) => (
              <DiFiCheckBox
                type="checkbox"
                label={addressTypeLabel(code)}
                value={code}
                checked={pState.addressForm.addressType === code}
                class="font-s color-D"
                style={{ width: '100%' }}
                onChange={() => selectAddressType(code)}
              />
            ))}
          </div>
        </OpenFormItem>

        <If when={isAddressTypeOther()}>
          <OpenFormItem field="correspondenceAddress" required>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={80}
              placeholder={i18n.OpenProgress.Info.Address.AddressTypeOtherPlaceholder}
              value={pState.addressForm.correspondenceAddress}
              onUpdateValue={(v) => setMailingDetail(v)}
            />
          </OpenFormItem>
        </If>

        <OpenFormItem label={i18n.OpenProgress.Info.Address.LabelPhone} field="phone" required>
          <div>
            <DiFiInput
              shellClass="f-input-2"
              type="tel"
              inputKind="digit"
              maxLength={15}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.addressForm.phone}
              onUpdateValue={(v) => (pState.addressForm.phone = v)}
              prefixRender={() => {
                const raw = String(pState.addressForm.phoneCountry || '').trim();
                const dial = raw.startsWith('+')
                  ? raw
                  : /^[A-Za-z]{2,3}$/.test(raw)
                    ? dialFromCountryValue(raw) || raw
                    : toDialCode(raw);
                return (
                  <div class="flex align-center flex-shrink-0" onclick={openAreaCode}>
                    <span class={dial ? 'color-D font-s' : 'color-L font-s'}>
                      {dial || i18n.OpenProgress.Common.PlaceholderSelect}
                    </span>
                    <Icon name="common-arrow-down" class="icon-c-D margin-l-XXS" />
                  </div>
                );
              }}
            />
          </div>
        </OpenFormItem>

        <OpenFormItem label={i18n.OpenProgress.Info.Address.LabelEmail} field="email" required>
          <div>
            <DiFiInput
              shellClass="f-input-2"
              type="email"
              inputKind="email"
              maxLength={80}
              placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
              value={pState.addressForm.email}
              readonly={isAddressEmailLocked()}
              onUpdateValue={(v) => {
                if (isAddressEmailLocked()) return;
                pState.addressForm.email = v;
              }}
            />
          </div>
        </OpenFormItem>

        <div>
          <div class="font-s color-D flex align-center gap-4px margin-t-XL margin-b-XS">
            {i18n.OpenProgress.Info.Address.UploadProof}
            <DiFiTooltip
              content={i18n.OpenProgress.Info.Address.UploadProofTip}
              placement="top"
              autoFlip={false}
              ellipsis={false}
              style={{ width: 'auto', display: 'inline-flex', 'flex-shrink': 0 }}
            >
              <Icon name="common-info-empty" class="icon-c" />
            </DiFiTooltip>
          </div>

          <UploadProof
            useFor="address_prove"
            busId={pState.id}
            maxCount={5}
            imgs={getAddressImages()}
            change={({ url, action }) => {
              if (action === 'remove') {
                removeAttachmentFile('address_prove', url);
                return;
              }
              if (url) addAttachmentFile('address_prove', url);
            }}
          />
          <FieldReEditHint field="address_prove" />
        </div>
      </Form>

      <div class="bg-c-XD round padding-L margin-t-S color-D">
        <div class="flex justify-space-between margin-b-L font-s-S">
          <div class="">{i18n.OpenProgress.Info.Address.ExampleTitle}</div>
          {/* <div class="link-color">{i18n.OpenProgress.Info.Address.MoreExamples}</div> */}
        </div>

        <img class="margin-b-L width-100" src="/images/upload-exp.svg" />

        <div class="font-s-XS">
          <div>{i18n.OpenProgress.Info.Address.Guide1}</div>
          <div>{i18n.OpenProgress.Info.Address.Guide2}</div>
          <div>{i18n.OpenProgress.Info.Address.Guide3}</div>
        </div>
      </div>

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          last={() => {
            lastProcess();
          }}
          next={async () => {
            const urls = getAddressImages();
            if (!urls.length) {
              showToast(i18n.OpenProgress.Info.Address.ToastProofRequired);
              return;
            }
            const valid = await formApi.validate();
            if (!valid) return;
            const data = buildAddressSavePayload();
            await saveUserInfo({
              ...data,
              progress: 4,
              address_prove: urls.join(','),
            });
            nextProcess();
          }}
        />
      </If>
    </div>
  );
};
